/**
* @copyright Copyright (C) 2020 Kokoon - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*/

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { styled } from '@material-ui/core/styles';
import KnTextField from 'components/TextField';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { KnBoldSectionHeader } from 'components/Typography';
import KnLink from 'components/Link';
import KnButton from 'components/Button';
import { emailFormatValidator } from 'utils/customFieldValidators';
import loginActions from 'redux/actions/loginActions';
import { APP_PAGE_URLS } from 'Constants';

const initialSignInInfo = {
  email: '',
  password: '',
};

const KnForgotPasswordText = styled(Typography)({
  display: 'flex',
  alignItems: 'center',
});

const KnForgotPasswordHint = ({ children }) => (
  <KnForgotPasswordText color="textSecondary">{children}</KnForgotPasswordText>
);

const SignInStep = ({ submitDisabled, onSuccess }) => {
  const { t: translate } = useTranslation();
  const {
    handleSubmit, errors, control,
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialSignInInfo,
    reValidateMode: 'onBlur',
  });
  const dispatch = useDispatch();

  /** We can cache the validation rules, as they should change only if the
   * translation is updated so that the messages are in the correct language.
   */
  const emailValidationRules = useMemo(() => ({
    required: {
      value: true,
      message: translate('LOGIN.FIELD_VALIDATION_MESSAGES.email.required'),
    },
    validate: {
      emailFormat: emailFormatValidator,
    },
  }), [translate]);

  const passwordValidationRules = useMemo(() => ({
    required: {
      value: true,
      message: translate('LOGIN.FIELD_VALIDATION_MESSAGES.password.required'),
    },
  }), [translate]);

  /** This function will be called by handleSubmit with form data
   * only when the data is valid. We can make the signIn call now.
   */
  const onSubmit = (formData, e) => {
    e.preventDefault();
    dispatch(loginActions.signIn(formData))
      .then((response) => {
        if (!response.error) {
          /** Sign in was successful,
           * send response object to step 2 of the authentication flow */
          onSuccess({ signInResult: response });
        }
      });
  };

  return (
    <>
      <Typography variant="h6" component={KnBoldSectionHeader} marginBottom>
        {translate('LOGIN.title')}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={KnTextField}
          name="email"
          rules={emailValidationRules}
          label={translate('GENERAL.email')}
          inputProps={{ 'data-testid': 'email-input-field', maxLength: 256 }}
          InputLabelProps={{ 'data-testid': 'email-input-label' }}
          error={errors.email}
          defaultValue=""
          control={control}
        />
        <Controller
          as={KnTextField}
          label={translate('FIELD_LABELS.password')}
          type="password"
          name="password"
          rules={passwordValidationRules}
          error={errors.password}
          defaultValue=""
          control={control}
          inputProps={{ 'data-testid': 'password-input-field', maxLength: 256 }}
          InputLabelProps={{ 'data-testid': 'password-input-label' }}
        />
        <Trans
          parent={KnForgotPasswordHint}
          i18nKey="LOGIN.forgotPassword"
          components={[
            <KnLink
              color="textSecondary"
              route={APP_PAGE_URLS.forgotPassword}
              data-testid="forgot-password-link"
            />,
          ]}
        />
        <Box
          pt={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <KnButton
            variant="text"
            type="button"
            route={APP_PAGE_URLS.register}
            data-testid="register-link"
          >
            {translate('LOGIN.registerButton')}
          </KnButton>
          <KnButton
            disabled={submitDisabled}
            data-testid="login-button"
          >
            {translate('LOGIN.submitButton')}
          </KnButton>
        </Box>
      </form>
    </>
  );
};

SignInStep.propTypes = {
  submitDisabled: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

KnForgotPasswordHint.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SignInStep;
